import { useEffect, useRef, useState } from "react";
import { ImParagraphRight } from "react-icons/im";
import * as XLSX from "xlsx";
import { api, listarTudo } from "../../../services/api";
import { exportarRelatorioPDF } from "../../../utils/relatorioPdf";

const RelatorioEstoqueAtualizado = () => {
  const [dados, setDados] = useState({
    resumo: {
      total_itens: 0,
      valor_total_estoque: 0,
    },
    itens: [],
    por_categoria: [],
    movimentacoes_recentes: [],
  });
  const [showModal, setShowModal] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState("itens");

  const [categorias, setCategorias] = useState([]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = {
    categorias: useRef(null),
  };

  // Só "categorias" é filtro real: o backend de
  // /relatorios/almoxarifado/est-atualizado/ não tem fornecedor, localização,
  // status de estoque mínimo/máximo nem data de validade (EstoqueItem não
  // tem esses campos) — filtros por esses critérios eram aceitos e
  // ignorados silenciosamente, dando a impressão de que filtravam algo.
  const [filtros, setFiltros] = useState({
    categorias: [],
  });

  useEffect(() => {
    carregarOpcoes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openDropdown &&
        dropdownRefs[openDropdown]?.current &&
        !dropdownRefs[openDropdown].current.contains(event.target)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);

  const carregarOpcoes = async () => {
    try {
      const categoriasRes = await listarTudo("/almoxarifado/categorias/");
      setCategorias(
        Array.isArray(categoriasRes.data) ? categoriasRes.data : categoriasRes.data.results || [],
      );
    } catch (error) {
      console.error("Erro ao carregar opções:", error);
      setCategorias([]);
    }
  };

  const handleMultiSelect = (campo, id) => {
    setFiltros((prev) => {
      const current = prev[campo] || [];
      const updated = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      return { ...prev, [campo]: updated };
    });
  };

  const handleSelectAll = (campo, options) => {
    if (Array.isArray(options)) {
      const allIds = options.map((opt) => opt.id);
      setFiltros((prev) => ({ ...prev, [campo]: allIds }));
    }
  };

  const handleClearAll = (campo) => {
    setFiltros((prev) => ({ ...prev, [campo]: [] }));
  };

  const handleChange = (e) => {
    setFiltros({
      ...filtros,
      [e.target.name]: e.target.value,
    });
  };

  const buscarDados = async () => {
    setCarregando(true);

    try {
      const params = {};

      if (filtros.categorias && filtros.categorias.length > 0) {
        params.categorias = filtros.categorias.join(",");
      }

      console.log(
        "🌐 URL:",
        `/relatorios/almoxarifado/est-atualizado/?${new URLSearchParams(params)}`,
      );

      const response = await api.get(
        "/relatorios/almoxarifado/est-atualizado/",
        {
          params: params,
        },
      );

      console.log("🔍 Dados recebidos:", response.data);
      setDados(response.data);
      setShowModal(true);
    } catch (error) {
      console.error(error);
      alert("Erro ao gerar relatorio");
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => {
    setFiltros({
      categorias: [],
    });
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return "-";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);
  };

  const exportarExcel = () => {
    if (!dados || !dados.itens || dados.itens.length === 0) {
      alert("Nao ha dados para exportar!");
      return;
    }

    const workbook = XLSX.utils.book_new();

    // Planilha de Itens
    const wsItens = XLSX.utils.json_to_sheet(dados.itens);
    XLSX.utils.book_append_sheet(workbook, wsItens, "Itens em Estoque");

    // Planilha de Resumo por Categoria
    if (dados.por_categoria && dados.por_categoria.length > 0) {
      const wsCategoria = XLSX.utils.json_to_sheet(dados.por_categoria);
      XLSX.utils.book_append_sheet(workbook, wsCategoria, "Por Categoria");
    }

    XLSX.writeFile(workbook, "relatorio_estoque_atualizado.xlsx");
  };

  const exportarPDF = async () => {
    await exportarRelatorioPDF({
      titulo: "Relatório de Estoque Atualizado",
      colunas: [
        { key: "produto", label: "PRODUTO" },
        { key: "categoria", label: "CATEGORIA" },
        { key: "quantidade_atual", label: "QTD ATUAL" },
        { key: "unidade", label: "UNID" },
        { key: "custo_unitario", label: "CUSTO UNIT" },
        { key: "valor_total", label: "VALOR TOTAL" },
      ],
      dados: dados?.itens || [],
      formatarValor: (valor, coluna) =>
        coluna.key === "custo_unitario" || coluna.key === "valor_total"
          ? formatarMoeda(valor)
          : (valor ?? "-"),
    });
  };

  const imprimir = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Relatorio de Estoque Atualizado</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; font-size: 18px; }
            h2 { color: #555; font-size: 14px; margin-top: 20px; }
            .header-info { margin-bottom: 20px; }
            .resumo-cards { display: flex; gap: 15px; margin-bottom: 25px; flex-wrap: wrap; }
            .card { background: #f8f9fa; padding: 15px; border-radius: 8px; text-align: center; min-width: 140px; }
            .card .valor { font-size: 22px; font-weight: bold; margin-top: 5px; }
            .card .label { font-size: 11px; color: #666; }
            .card.verde .valor { color: #28a745; }
            .card.vermelho .valor { color: #dc3545; }
            .card.azul .valor { color: #17a2b8; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; margin-bottom: 25px; font-size: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .footer { margin-top: 20px; font-size: 10px; color: #666; text-align: center; }
            @media print {
              body { margin: 0; padding: 20px; }
              .card { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>Relatorio de Estoque Atualizado</h1>
          <div class="header-info">
            <p><strong>Gerado em:</strong> ${new Date().toLocaleString()}</p>
          </div>

          <div class="resumo-cards">
            <div class="card azul">
              <div class="label">📦 Total de Itens</div>
              <div class="valor">${dados.resumo?.total_itens || 0}</div>
            </div>
            <div class="card azul">
              <div class="label">💰 Valor Total do Estoque</div>
              <div class="valor">${formatarMoeda(dados.resumo?.valor_total_estoque)}</div>
            </div>
          </div>

          <h2>📋 Itens em Estoque</h2>
          <table>
            <thead>
              <tr>
                <th>PRODUTO</th>
                <th>CATEGORIA</th>
                <th>QTD ATUAL</th>
                <th>UNID</th>
                <th>CUSTO UNIT</th>
                <th>VALOR TOTAL</th>
              </tr>
            </thead>
            <tbody>
              ${dados.itens
                ?.map((item) => `
                <tr>
                  <td>${item.produto || "-"}</td>
                  <td>${item.categoria || "-"}</td>
                  <td>${item.quantidade_atual || 0}</td>
                  <td>${item.unidade || "-"}</td>
                  <td>${formatarMoeda(item.custo_unitario)}</td>
                  <td>${formatarMoeda(item.valor_total)}</td>
                </tr>
              `)
                .join("")}
            </tbody>
          </table>

          ${
            dados.por_categoria && dados.por_categoria.length > 0
              ? `
          <h2>📊 Resumo por Categoria</h2>
          <table>
            <thead><tr><th>CATEGORIA</th><th>QTD ITENS</th><th>VALOR TOTAL</th></tr></thead>
            <tbody>
              ${dados.por_categoria
                .map(
                  (cat) => `
                <tr>
                  <td>${cat.categoria || "-"}</td>
                  <td>${cat.quantidade_itens || 0}</td>
                  <td>${formatarMoeda(cat.valor_total)}</td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
          `
              : ""
          }

          <div class="footer">Sistema de Gestao Agropecuaria - Relatorio Gerado Automaticamente</div>
          <script>
            window.onload = () => { window.print(); setTimeout(() => window.close(), 100); }
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const MultiSelect = ({
    label,
    options,
    selectedValues,
    fieldName,
    refProp,
  }) => {
    const optionsArray = Array.isArray(options) ? options : [];
    const selectedArray = Array.isArray(selectedValues) ? selectedValues : [];

    const getSelectedNames = () => {
      const selected = optionsArray.filter((opt) =>
        selectedArray.includes(opt.id),
      );
      if (selected.length === 0) return `Selecione ${label.toLowerCase()}`;
      if (selected.length <= 2) return selected.map((s) => s.nome).join(", ");
      return `${selected.length} ${label.toLowerCase()} selecionados`;
    };

    return (
      <div className="relative" ref={refProp}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
        <div
          onClick={() =>
            setOpenDropdown(openDropdown === fieldName ? null : fieldName)
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <span className="text-gray-700">{getSelectedNames()}</span>
          <span className="float-right">▼</span>
        </div>

        {openDropdown === fieldName && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => handleClearAll(fieldName)}
                className="text-sm text-blue-600 hover:text-blue-800 mr-3"
              >
                Limpar todos
              </button>
              <button
                onClick={() => handleSelectAll(fieldName, optionsArray)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Selecionar todos
              </button>
            </div>
            {optionsArray.map((option) => (
              <label
                key={option.id}
                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedArray.includes(option.id)}
                  onChange={() => handleMultiSelect(fieldName, option.id)}
                  className="mr-3 h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <span className="text-gray-700">{option.nome}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <ImParagraphRight size={38} />
              Relatorio de Estoque Atualizado
            </h2>
            <p className="text-gray-600 mt-2">
              Consulte o estoque atual e as movimentações recentes
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <MultiSelect
                label="Categorias"
                options={categorias}
                selectedValues={filtros.categorias}
                fieldName="categorias"
                refProp={dropdownRefs.categorias}
              />
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={buscarDados}
                disabled={carregando}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-2 px-4 rounded-lg"
              >
                {carregando ? "Carregando..." : "Gerar Relatorio"}
              </button>
              <button
                onClick={limparFiltros}
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-7xl my-8 flex flex-col"
            style={{ maxHeight: "calc(100vh - 4rem)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">
                Relatorio de Estoque Atualizado
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center"
              >
                ✕
              </button>
            </div>

            <div className="border-b border-gray-200 px-6">
              <div className="flex gap-4">
                <button
                  onClick={() => setAbaAtiva("itens")}
                  className={`py-3 px-4 font-medium ${abaAtiva === "itens" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500"}`}
                >
                  📦 Itens em Estoque
                </button>
                <button
                  onClick={() => setAbaAtiva("categorias")}
                  className={`py-3 px-4 font-medium ${abaAtiva === "categorias" ? "border-b-2 border-green-500 text-green-600" : "text-gray-500"}`}
                >
                  📊 Por Categoria
                </button>
              </div>
            </div>

            <div className="p-6 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-3">
              <button
                onClick={exportarExcel}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg"
              >
                📊 Exportar Excel
              </button>
              <button
                onClick={exportarPDF}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg"
              >
                📄 Exportar PDF
              </button>
              <button
                onClick={imprimir}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg"
              >
                🖨️ Imprimir
              </button>
            </div>

            <div className="flex-1 overflow-auto p-6">
              {abaAtiva === "itens" && (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-700">
                        {dados.resumo?.total_itens || 0}
                      </div>
                      <div className="text-xs text-blue-600">Total Itens</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-700">
                        {formatarMoeda(dados.resumo?.valor_total_estoque)}
                      </div>
                      <div className="text-xs text-green-600">Valor Total</div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100 border-b-2 border-gray-300">
                          <th className="px-4 py-3 text-left text-sm font-semibold">
                            PRODUTO
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">
                            CATEGORIA
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">
                            QTD
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">
                            UNID
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">
                            CUSTO UNIT
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">
                            TOTAL
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dados.itens?.map((item, idx) => (
                          <tr
                            key={idx}
                            className="border-b border-gray-200 hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 text-sm">
                              {item.produto || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {item.categoria || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium">
                              {item.quantidade_atual || 0}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {item.unidade || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {formatarMoeda(item.custo_unitario)}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold">
                              {formatarMoeda(item.valor_total)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {abaAtiva === "categorias" && (
                <div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100 border-b-2 border-gray-300">
                          <th className="px-4 py-3 text-left text-sm font-semibold">
                            CATEGORIA
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">
                            QUANTIDADE DE ITENS
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">
                            VALOR TOTAL
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dados.por_categoria?.map((cat, idx) => (
                          <tr key={idx} className="border-b border-gray-200">
                            <td className="px-4 py-3 text-sm">
                              {cat.categoria || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {cat.quantidade_itens || 0}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold">
                              {formatarMoeda(cat.valor_total)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RelatorioEstoqueAtualizado;
